import {MessageDescriptor} from '@lingui/core';

import {CustomFieldsEnum} from '../../../../models/api/customFieldModel';
import {
  RuleCompoundConditions,
  RuleCompoundConditionTypesEnum,
  SimpleCompoundConditions,
} from '../../../../models/api/ruleModel';
import {SettingsCustomFieldsPagesEnum} from '../../../../models/url/settingsUrlModel';
import {CustomFields} from '../../../editors/commons/customFieldsModel';
import {AtomParams, PreAtom} from '../../../editors/expressions/expressionAtomModel';
import {TransformerIdsEnum} from '../../../editors/expressions/expressionInferHelpers';
import {PreExpression} from '../../../editors/expressions/expressionModel';
import {RuleAction, RuleActionTypesEnum} from '../../ruleActionModel';
import {RuleCondition, RuleConditionTypesEnum} from '../../ruleConditionModel';
import {RuleEventTypesEnum} from '../../ruleEventModel';
import {getRequiredFeaturesForRule} from '../../ruleHelpers';
import {RuleIntegration} from '../../ruleIntegrationModel';
import {RuleRecipe, RuleRecipeCategoriesEnum} from './index';

// Used to declare recipes easily
export interface LooseRuleRecipe {
  id: string;
  title: MessageDescriptor;
  description: MessageDescriptor;
  categories: ReadonlyArray<RuleRecipeCategoriesEnum>;
  placeholders?: ReadonlyArray<RuleRecipePlaceholder>;
  // Used to display large cards
  imageUrl?: string;
  rule: {
    conditions: RecipeCompoundConditions | RecipeCondition;
    actions: ReadonlyArray<RuleRecipeAction>;
    events: ReadonlyArray<RuleEventTypesEnum>;
    expressions?: ReadonlyArray<PreExpression>;
  };
}

// Arrays of union types are janky in TypeScript. Improvements are welcome !
type RecipeCompoundConditions = RuleCompoundConditions<RecipeCondition>;
type RecipeSimpleCompoundCondition = SimpleCompoundConditions<RecipeCondition>;
type RecipeConditionParameter = RecipeSimpleCompoundCondition | RecipeCondition;

export function createCompoundRuleAndClause(
  ...args: [RecipeCondition, RecipeCondition, ...RecipeCondition[]]
): RecipeSimpleCompoundCondition;
export function createCompoundRuleAndClause(
  ...args: [RecipeSimpleCompoundCondition, RecipeCondition, ...RecipeConditionParameter[]]
): RecipeCompoundConditions;
export function createCompoundRuleAndClause(
  ...args: [RecipeCondition, RecipeSimpleCompoundCondition, ...RecipeConditionParameter[]]
): RecipeCompoundConditions;
export function createCompoundRuleAndClause(
  ...args: [RecipeConditionParameter, RecipeConditionParameter, ...RecipeConditionParameter[]]
): RecipeCompoundConditions {
  return [RuleCompoundConditionTypesEnum.AND, ...args];
}

export function createCompoundRuleOrClause(
  ...args: [RecipeCondition, RecipeCondition, ...RecipeCondition[]]
): RecipeSimpleCompoundCondition;
export function createCompoundRuleOrClause(
  ...args: [RecipeSimpleCompoundCondition, RecipeCondition, ...RecipeConditionParameter[]]
): RecipeCompoundConditions;
export function createCompoundRuleOrClause(
  ...args: [RecipeCondition, RecipeSimpleCompoundCondition, ...RecipeConditionParameter[]]
): RecipeCompoundConditions;
export function createCompoundRuleOrClause(
  ...args: [RecipeConditionParameter, RecipeConditionParameter, ...RecipeConditionParameter[]]
): RecipeCompoundConditions {
  return [RuleCompoundConditionTypesEnum.OR, ...args];
}

function isCompound<T>(c: RuleCompoundConditions<T> | T): c is RuleCompoundConditions<T> {
  return Array.isArray(c);
}

function processActions(
  actions: ReadonlyArray<RuleRecipeAction>,
  integrations: RuleIntegration,
): ReadonlyArray<RuleAction> {
  return actions.map((action): RuleAction => {
    const integration = integrations.getIntegrationByName(action.integration);

    const simpleAction = {
      type: action.type as RuleAction['type'],
      integration_id: integration?.id ?? 0,
    };

    const actionDefinition = integrations.findActionDefinition(simpleAction);

    return {
      ...simpleAction,
      params: action.params ?? actionDefinition?.defaultParams ?? {},
      restricted: actionDefinition?.restricted,
    } as RuleAction;
  });
}

function mapSimpleConditions<T, U>(
  conditions: SimpleCompoundConditions<T>,
  f: (value: T) => U,
): SimpleCompoundConditions<U> {
  const [operator, ...rules] = conditions;
  return [operator, ...rules.map((rule) => f(rule))];
}

function mapConditions<T, U>(
  conditions: RuleCompoundConditions<T>,
  f: (value: T) => U,
): RuleCompoundConditions<U> {
  const [operator, ...rules] = conditions;
  const mappedRules = rules.map((rule) => (isCompound(rule) ? mapSimpleConditions(rule, f) : f(rule)));
  return [operator, ...mappedRules];
}

function processConditions(
  conditions: RecipeCompoundConditions | RecipeCondition,
  integrations: RuleIntegration,
): RuleCompoundConditions {
  const compoundConditions: RecipeCompoundConditions = isCompound(conditions)
    ? conditions
    : [RuleCompoundConditionTypesEnum.AND, conditions];

  return mapConditions(compoundConditions, (recipeCondition) => {
    const integration = integrations.getIntegrationByName(recipeCondition.integration);

    const simpleCondition = {
      type: recipeCondition.type as RuleCondition['type'],
      integration_id: integration?.id ?? 0,
    };

    const conditionDefinition = integrations.findConditionDefinition(simpleCondition);

    return {
      ...simpleCondition,
      params: recipeCondition.params ?? conditionDefinition?.defaultParams ?? {},
      restricted: conditionDefinition?.restricted,
    } as RuleCondition;
  });
}

export function resolvePreExpressions(
  customFields: CustomFields,
  expressions: ReadonlyArray<PreExpression>,
  placeholders: ReadonlyArray<RuleRecipePlaceholder>,
) {
  return expressions.map((expression) => ({
    ...expression,
    atoms: resolvePreAtoms(customFields, expression.atoms, placeholders),
  }));
}

function resolvePreAtoms(
  customFields: CustomFields,
  atoms: ReadonlyArray<PreAtom>,
  placeholders: ReadonlyArray<RuleRecipePlaceholder>,
) {
  return atoms.map((atom) => {
    if (atom.transform === TransformerIdsEnum.GET_CUSTOM_FIELD) {
      return {
        ...atom,
        params: {
          ...atom.params,
          custom_field: resolveCustomFieldId(customFields, placeholders, atom.params),
        },
      };
    }
    return atom;
  });
}

function resolveCustomFieldId(
  customFields: CustomFields,
  placeholders: ReadonlyArray<RuleRecipePlaceholder>,
  atomParams?: AtomParams,
) {
  const placeholder = placeholders.find(
    ({type, id}) => type === RuleRecipePlaceholderTypesEnum.CUSTOM_FIELD && id === atomParams?.custom_field,
  );

  if (!placeholder) {
    return undefined;
  }

  const {type, resourceType, name} = placeholder.params;
  const customField = customFields[resourceType].find((field) => field.name === name && field.type === type);

  // TODO @smart_rules: Create custom field if it does not exist.
  if (!customField) {
    return undefined;
  }

  return customField.id;
}

export function processRecipes(
  recipes: ReadonlyArray<LooseRuleRecipe>,
  integrations: RuleIntegration,
): ReadonlyArray<RuleRecipe> {
  return recipes
    .map((recipe) => ({
      ...recipe,
      rule: {
        ...recipe.rule,
        actions: processActions(recipe.rule.actions, integrations),
        conditions: processConditions(recipe.rule.conditions, integrations),
      },
    }))
    .map((recipe) => ({
      ...recipe,
      requiredFeatures: getRequiredFeaturesForRule({
        ...recipe.rule,
        compoundConditions: recipe.rule.conditions,
        expressions: recipe.rule.expressions || [],
      }),
    }));
}

// Type helpers to map the actions / condition union to their own parameters.
export type DiscriminateUnion<T, K extends keyof T, V extends T[K]> = T extends Record<K, V> ? T : never;
export type MapDiscriminatedUnion<T extends Record<K, string>, K extends keyof T> = {
  [V in T[K]]: DiscriminateUnion<T, K, V>;
};

type ConditionType = RuleCondition['type'];
type ConditionMap = MapDiscriminatedUnion<RuleCondition, 'type'>;
type ConditionParams<T extends ConditionType> = ConditionMap[T]['params'];

type ActionType = RuleAction['type'];
type ActionMap = MapDiscriminatedUnion<RuleAction, 'type'>;
type ActionParams<T extends ActionType> = ActionMap[T]['params'];

interface RuleRecipeAction {
  type: RuleActionTypesEnum;
  params?: unknown;
  integration: string;
}

export function createRuleRecipeAction<T extends ActionType>(
  type: T,
  params?: ActionParams<T>,
  integration = 'default',
): RuleRecipeAction {
  return {
    type,
    params,
    integration,
  };
}

interface RecipeCondition {
  type: RuleConditionTypesEnum;
  params?: unknown;
  integration: string;
}

export function createRuleRecipeCondition<T extends ConditionType>(
  type: T,
  params?: ConditionParams<T>,
  integration = 'default',
): RecipeCondition {
  return {
    type,
    params,
    integration,
  };
}

export function createRuleRecipeExpression(
  id: string,
  name: string,
  atoms: ReadonlyArray<PreAtom>,
): PreExpression {
  return {
    id,
    name,
    atoms,
  } as PreExpression;
}

export enum RuleRecipePlaceholderTypesEnum {
  CUSTOM_FIELD = 'custom_field',
}
interface CustomFieldPlaceholderParams {
  type: CustomFieldsEnum;
  resourceType: SettingsCustomFieldsPagesEnum;
  name: string;
  defaultDescription: string;
}

export interface RuleRecipePlaceholderParams {
  [RuleRecipePlaceholderTypesEnum.CUSTOM_FIELD]: CustomFieldPlaceholderParams;
}

export type RuleRecipePlaceholder<T extends RuleRecipePlaceholderTypesEnum = RuleRecipePlaceholderTypesEnum> =
  {
    id: string;
    type: T;
    params: RuleRecipePlaceholderParams[T];
  };

export function hasRuleRecipePlaceholders(recipe: RuleRecipe) {
  return Boolean(recipe.placeholders?.length);
}
